import React from "react"
import { BodyText, InlineText, SubTitle } from "../../Typography/Typography"
import SmartLink from "../../layout/SmartLink/SmartLink"

const providerProfileClickEvent = () => ({
  name: "SEO Filtered Directory - Copy link to provider clicked",
  properties: {
    "Care Type": "care_home",
    "GEO Location": "Sheffield",
    "Current URL":
      "https://www.caresourcer.com/s/providers/towns/sheffield/care-home/",
  },
})

export default {
  summary: (
    <BodyText marginBottom>
      Choosing the right care home for someone close to you isn&apos;t easy.
      Luckily, Sheffield has over 100 care homes, and more than 80 of these have
      top ratings from the Care Quality Commission (CQC). The best choice really
      depends on what your loved one needs. Here&apos;s some useful advice to
      help you pick the right place.
    </BodyText>
  ),
  content: [
    <SubTitle marginBottom bold>
      Location is key
    </SubTitle>,
    <BodyText marginBottom>
      It&apos;s key to pick a care home that feels just right. Whether it&apos;s
      being near family and friends or staying in a familiar area, Sheffield has
      plenty of options thanks to its great transport connections. You might
      find just what you&apos;re looking for in the heart of the city like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-1477142381/brackenfield-hall"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Brackenfield Hall</InlineText>
      </SmartLink>
      , or perhaps something quieter near the Peak District, such as{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-2454435588/henleigh-hall-care-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Henleigh Hall Care Home</InlineText>
      </SmartLink>
      . It&apos;s a good idea to think about where visitors will be coming from
      and check what the transport links are like nearby.
    </BodyText>,
    <SubTitle marginBottom bold>
      Feeling at home
    </SubTitle>,
    <BodyText marginBottom>
      Think about what makes your loved one happy. Do they enjoy quiet time
      watching TV or reading? Or are they happier being around people and
      staying active? Here are a few things to consider:
    </BodyText>,
    <SubTitle marginBottom bold italic>
      Gardens and outdoor spaces
    </SubTitle>,
    <BodyText marginBottom>
      If your loved one enjoys gardening or simply loves being outdoors, several
      care homes in Sheffield encourage residents to get involved with
      gardening, like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-228749739/abbey-grange-nursing-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Abbey Grange Nursing Home</InlineText>
      </SmartLink>
      , for example. Or maybe they would like a nice spot to enjoy the afternoon
      sun. Places like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-513878659/belmont-house"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Belmont House</InlineText>
      </SmartLink>{" "}
      might have smaller gardens, but offer lovely areas to sit outside. When
      you visit a care home, either in person or virtually, try to picture your
      loved one enjoying the outdoor spaces they have available.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Pets welcome
    </BodyText>,
    <BodyText marginBottom>
      Many care homes in Sheffield understand how important pets can be and
      organise visits from friendly animals to cheer up the residents. Some
      places, like{" "}
      <SmartLink
        to="https://www.caresourcer.com/uk/providers/cqc/1-319142721/ascot-lodge-nursing-home"
        newTab
        clickEvent={providerProfileClickEvent()}
      >
        <InlineText brand>Ascot Lodge Nursing Home</InlineText>
      </SmartLink>
      , even let personal pets come by, as long as you arrange it first. If your
      loved one adores animals, it&apos;s worth asking about the home&apos;s
      policy on pets.
    </BodyText>,
    <BodyText marginBottom bold italic>
      Activities and days out
    </BodyText>,
    <BodyText marginBottom>
      It&apos;s important for care homes to offer a mix of quiet spaces and
      areas full of life. Sheffield&apos;s care homes are great at this, with
      facilities like libraries, cinemas and hair salons, as well as lots of
      group activities. They also try to arrange trips out to places like the
      Botanical Gardens or a show at the Lyceum Theatre, when they can.
    </BodyText>,
  ],
}
